import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { getSelectedEventStudyGroupFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { GROUP_BY_DEFAULT_ITEM_KEY } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedStringValuePrimaryFilterFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import React, { useCallback, useMemo } from 'react';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { FilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { getAllowedGroupBySelectionsForTableView } from 'libs/end-to-end/src/table-view-utils';

export const GroupByMenu = ({
  store,
  feature,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
  focusArea,
  analysisName,
  secondaryFilterDefinitions,
  allDimensions,
  primaryFilterKey = 'groupBy',
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  allDimensions: DimensionDefinition[];
  primaryFilterKey?: PrimaryFilterKey;
}) => {
  const language = useLanguage();

  const items: FilterListBoxItem[] = useMemo(() => {
    const currentGroupBy = getSelectedStringValuePrimaryFilterFromStore({
      feature,
      organizationId,
      databaseRevisionId,
      databaseId,
      store,
      focusArea,
      analysisName,
      primaryFilterKey,
    });

    const allowedItems = getAllowedGroupBySelectionsForTableView({
      allDimensions,
      secondaryFilterDefinitions,
      language,
      includeNoSelection:
        primaryFilterKey === 'secondaryBreakdown' || feature === 'tableView',
    });

    const mappedItems = allowedItems.map((item) => ({
      value: item.value,
      translatedContent: item.displayName,
      selected: item.value === currentGroupBy,
    }));

    if (feature === 'timeSeriesTrendsPrimary') {
      const overallAverageItem: FilterListBoxItem = {
        value: GROUP_BY_DEFAULT_ITEM_KEY,
        translatedContent: 'Overall Average',
        selected: currentGroupBy === GROUP_BY_DEFAULT_ITEM_KEY,
      };
      return [overallAverageItem, ...mappedItems];
    }

    return mappedItems;
  }, [
    feature,
    organizationId,
    databaseRevisionId,
    databaseId,
    store,
    focusArea,
    analysisName,
    secondaryFilterDefinitions,
    allDimensions,
    language,
    primaryFilterKey,
  ]);

  const getOtherFilterSelectionsThatNeedToUpdate = useCallback(
    (groupBySelection: string): PrimaryFilterSelection[] => {
      if (feature !== 'timeSeriesTrendsPrimary') {
        return [];
      }

      if (!groupBySelection || groupBySelection === GROUP_BY_DEFAULT_ITEM_KEY) {
        return [
          {
            primaryFilterKey: 'eventStudyGroup',
            value: [],
          },
        ];
      }

      const selectedEventStudyGroup = getSelectedEventStudyGroupFromStore({
        store,
        feature,
        organizationId,
        databaseRevisionId,
        databaseId,
      });

      if (!selectedEventStudyGroup) {
        return [];
      }

      if (groupBySelection !== selectedEventStudyGroup.dimensionOrFilterName) {
        return [
          {
            primaryFilterKey: 'eventStudyGroup',
            value: [],
          },
        ];
      }

      return [];
    },
    [databaseId, databaseRevisionId, feature, organizationId, store],
  );

  return (
    <FilterMenuSinglePanel
      filterContent={
        <FilterListBox
          multiple={false}
          items={items}
          onUpdate={({ addedSelections }) => {
            const addedSelectionValue = addedSelections[0];
            const primaryFiltersSelections: PrimaryFilterSelection[] = [
              {
                primaryFilterKey,
                value: addedSelectionValue,
              },
            ];

            const extraFilterSelections =
              getOtherFilterSelectionsThatNeedToUpdate(addedSelectionValue);

            setPrimaryFilters({
              selections: [
                ...primaryFiltersSelections,
                ...extraFilterSelections,
              ],
            });

            closeMenu();
          }}
        />
      }
      bottomProps={'none'}
    />
  );
};
